import { extname } from "pathe";
import type { ReactNode } from "react";

import { MB } from "@/appConstant";
import { Trans } from "@/features/i18n/Trans";
import { toFileSizeDisplayString } from "@/lib/toFileSizeDisplayString";
import type { Feature } from "@/resources/attachment/uploadToFirebaseStorage";

const validationRules: Record<
  Feature,
  {
    maxFileSize: number;
    allowedExtensions: ReadonlyArray<string>;
  }
> = {
  userAvatar: {
    maxFileSize: 1 * MB,
    allowedExtensions: ["jpg", "jpeg", "png"],
  },
  brandLogo: {
    maxFileSize: 1 * MB,
    allowedExtensions: ["jpg", "jpeg", "png"],
  },
  launcherWidget: {
    maxFileSize: 1 * MB,
    allowedExtensions: ["jpg", "jpeg", "png"],
  },
  webStreamHero: {
    maxFileSize: 5 * MB,
    allowedExtensions: ["jpg", "jpeg", "png"],
  },
};

export type FileInvalidatedError =
  | {
      code: "exceedFileSize";
      maxFileSize: number;
      reactNode: ReactNode;
    }
  | {
      code: "unsupportedFileType";
      reactNode: ReactNode;
    };

const unSupportedFileTypeError: FileInvalidatedError = {
  code: "unsupportedFileType",
  reactNode: <Trans i18nKey="resource.attachment.unsupportedFileType" />,
};

function getExceedFileSizeError(maxFileSize: number): FileInvalidatedError {
  return {
    code: "exceedFileSize",
    maxFileSize,
    reactNode: (
      <Trans
        i18nKey="resource.attachment.fileSizeExceedWithSize"
        values={{ size: toFileSizeDisplayString(maxFileSize) }}
      />
    ),
  };
}

export function isUnSupportedFileTypeError(
  error: FileInvalidatedError,
): error is Extract<FileInvalidatedError, { code: "unsupportedFileType" }> {
  return error.code === "unsupportedFileType";
}

export function isExceedFileSizeError(
  error: FileInvalidatedError,
): error is Extract<FileInvalidatedError, { code: "exceedFileSize" }> {
  return error.code === "exceedFileSize";
}

function getFormattedExtname(fileName: string): string {
  return extname(fileName).slice(1).toLowerCase();
}

function validateFileSize(
  file: File,
  maxSize: number,
): FileInvalidatedError | null {
  if (file.size > maxSize) {
    return getExceedFileSizeError(maxSize);
  }
  return null;
}

function validateFileType(
  file: File,
  allowedExtensions: ReadonlyArray<string>,
): FileInvalidatedError | null {
  const ext = getFormattedExtname(file.name);
  const hasValidExt = allowedExtensions.some(
    (allowedExt) => ext === allowedExt.toLowerCase(),
  );
  if (!hasValidExt) {
    return unSupportedFileTypeError;
  }
  return null;
}

export async function validateImage(
  feature: Feature,
  file: File,
): Promise<FileInvalidatedError | null> {
  const rules = validationRules[feature];

  const validateFileTypeError = validateFileType(file, rules.allowedExtensions);
  if (validateFileTypeError) return validateFileTypeError;
  return validateFileSize(file, rules.maxFileSize);
}
